import { Deserializer } from "jsonapi-serializer";
import { DynamicFieldDescriptor } from "../models/DynamicFieldDescriptor";
import { genericError } from "../models/GenericError";
import { ListQuery } from "../models/ListQuery";
import { APIConnector } from "./api/APIConnector";
import { createPagination } from "./utilities/paginationBuilder";
import { processServerError, getConstraintViolation } from "./utilities/ServiceError";
import { createPartialURL, getFilterClause, appendFilterString } from "./utilities/urlBuilder";
import { isSuccessStatus } from "../utilities/requestResponseValidator";
import PresetCustomField from "../models/PresetCustomField";
import {MODE_TYPE_ARMED} from "../constants/mode-type";

export const apiConnector = new APIConnector();

export const getModesWithPagination = async (listParameters?: ListQuery): Promise<any> => {
    try {
        let dynamicFilter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const baseFilter = `id=out=(${MODE_TYPE_ARMED})`;
        const combinedFilter = appendFilterString(dynamicFilter, baseFilter);
        const partialURL = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, combinedFilter);
        const modeTypeFields = 'fields[modeType]=trnName,settingType';
        let url = `modeType?include=settingType&${modeTypeFields}${partialURL}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (error: any, data: any) => {
                if (error) {
                    return reject({ status: response.status, message: genericError });
                }
                const modes: any[] = data.map((setting): any => {
                    return {
                        id: setting['id'],
                        name: setting['trn-name'],
                        columnId: setting['setting-type']['id'],
                    };
                });
                return resolve({ rows: modes, pagination })
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

const buildSettingData = (settingDataId: any, settingData: any) => {
    return {
        type: "settingData",
        id: settingDataId ? settingDataId : 0,
        attributes: {
            sdValstring: settingData["value"]
        },
        relationships: {
            element: {
                data: {
                    type: "element",
                    id: settingData['id']
                }
            }
        }
    };
}

export const updatePreset = async (newPreset: PresetCustomField): Promise<any> => {
    try {
        const settingDataUrl = 'settingData';
        let settingDataResponses: Response[] = new Array(0);
        const settingDataPromises = newPreset.custom
            ? Object.values<any>(newPreset.custom)
                .filter(settingData =>
                    (!!settingData['value'] || !!settingData['settingDataId']))
                .map((settingData) => {
                    const settingDataId = settingData["settingDataId"] ?? 0;
                    const newData = buildSettingData(settingDataId, settingData)
                    const newSettingData = {
                        data: (+settingDataId) > 0 ? newData : [newData]
                    }
                    const updatedUrl = (+settingDataId) > 0 ? `${settingDataUrl}/${settingDataId}` : settingDataUrl;
                    return apiConnector.sendData(updatedUrl, !((+settingDataId) > 0), newSettingData);
                })
            : new Array(0);
        if (settingDataPromises.length > 0) {
            settingDataResponses = await Promise.all(settingDataPromises.flat());
        }

        return new Promise(async (resolve, reject) => {
            if (settingDataResponses.filter(response => !response || isSuccessStatus(response.status) === false).length > 0) {
                return reject({ id: '', success: false });
            }
            else {
                return resolve({ id: '', success: true });
            }
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getOutputPresetById = async (id: string, profileId: string): Promise<any> => {
    try {
        const url = `modeType/${id}?include=settingType`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();

        return new Promise((resolve, reject) => {
            // return the id for element relationship to be used later.
            new Deserializer({
                element: {
                    valueForRelationship: (relationship) => {
                        return { id: relationship.id }
                    }
                }
            }).deserialize(json, async (error: any, modeType: any) => {
                if (error) {
                    return reject({ status: response.status, message: genericError });
                }
                const modeTypeSettingTypeId = modeType['setting-type']?.['id'];
                const behaviorUrl = `behaviorType?include=modeType,settingType&filter=modeType.settingType.id==${modeTypeSettingTypeId}`;
                const behaviorTypeResponse = await apiConnector.fetchData(behaviorUrl);
                const behaviorTypeJson = await behaviorTypeResponse.json();

                new Deserializer().deserialize(behaviorTypeJson, async (error: any, behaviourRecords: any[]) => {
                    if (error) {
                        return reject({ status: response.status, message: genericError });
                    }

                    const behaviourSettingTypeIds = buildBehaviourSettingTypeIds(behaviourRecords);
                    const elementsUrl = `setting?include=settingType,profile,settingDatas.element.uiType,settingDatas.element.elementAttributes&filter=settingType.id=in=(${behaviourSettingTypeIds});profile.id==${profileId}`;
                    const elementsResponse = await apiConnector.fetchData(elementsUrl);
                    const elementsJson = await elementsResponse.json();

                    new Deserializer().deserialize(elementsJson, async (error: any, apiElementNames: any[]) => {
                        if (error) {
                            return reject(error);
                        }

                        const builtSettingData = buildSettingDataValues(apiElementNames);
                        const builtElementData = buildElementData(apiElementNames);
                        const groupElementFields = buildCustomFieldElements(builtElementData);

                        const preset: PresetCustomField = buildPreset(modeType, builtSettingData, builtElementData);
                        const buildCustomData = { ...builtSettingData, ...preset.custom }
                        return resolve({ preset, customData: buildCustomData, elementFields: groupElementFields });
                    });
                });
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

export const buildPreset = (apiPreset, apiSettingDatas?, apiElementNames?): PresetCustomField => {
    const newCustom = apiSettingDatas && apiElementNames
        ? apiElementNames.reduce((customFragment, elementName) => {
            customFragment[elementName['e-sysname']] = {
                id: elementName['id'],
                value: elementName['sdValue'] ?? '',
                settingDataId: elementName['settingDataId'] ?? ''
            }
            return customFragment;
        }, {})
        : {}

    return {
        id: apiPreset['id'],
        name: apiPreset['trn-name'],
        type: apiPreset['setting-type']?.['trn-name'],
        typeId: apiPreset['setting-type']?.['id'],
        custom: newCustom,
        sdist: apiPreset['s-dist'],
        profileId: apiPreset['profile']?.['id'],
        settingIdToCopy: apiPreset['setting-id-to-copy']
    };
};

const buildBehaviourSettingTypeIds = (behaviourRecords) => {
    let behaviorSettingTypeIds = '';
    behaviourRecords.map((behavior) => {
        behaviorSettingTypeIds = behaviorSettingTypeIds + behavior['setting-type']?.['id'] + ",";
    });

    const formatedBehaviourSettingTypeIds = behaviorSettingTypeIds.substr(0, behaviorSettingTypeIds.length - 1);
    return formatedBehaviourSettingTypeIds;
}

const buildSettingDataValues = (apiElementNames) => {
    let builtSettingData = [] as any;
    apiElementNames.filter((apiElementNames) => {
        apiElementNames['setting-datas'].filter((settingData) => {
            builtSettingData.push(settingData);
        })
    });

    return builtSettingData;
}

const buildElementData = (apiElementNames) => {
    let builtElementData = [] as any;

    apiElementNames.filter((apiElementNames) => {
        apiElementNames['setting-datas'].filter((settingData) => {
            const builtDatas = {
                'id': settingData['element']['id'],
                'e-hidden': settingData['element']['e-hidden'],
                'e-max': settingData['element']['e-max'],
                'e-ordinal': settingData['element']['e-ordinal'],
                'e-required': settingData['element']['e-required'],
                'e-sysname': apiElementNames['trn-name'],
                'e-units': settingData['element']['e-units'],
                'element-attributes': settingData['element']['element-attributes'],
                // 'name-concat': settingData['element']['name-concat'],
                'trn-name': settingData['element']['trn-name'],
                'setting-type': settingData['element']['setting-type'],
                'ui-type': settingData['element']['ui-type'],
                'elementDisplayName': apiElementNames['trn-name'],
                'sysName': settingData['element']['e-sysname'],
                'settingDataId': settingData['id'],
                'sdValue': settingData['sd-valstring']
            }
            builtElementData.push(builtDatas);
        })
    })
    const O_OUTPUT_PORT_NO = 'O_OUTPUT_PORT_NO'
    const fields = builtElementData.filter(item => !item.sysName?.includes(O_OUTPUT_PORT_NO));
    return fields;
}

const buildCustomFieldElements = (builtElementData) => {
    const fieldDescriptors: DynamicFieldDescriptor[] = builtElementData.map((apiElement): DynamicFieldDescriptor => {
        const options = apiElement['element-attributes']
            ? apiElement['element-attributes'].map(elementAttribute => {
                return { name: elementAttribute['trn-name'], id: elementAttribute['ea-valstring'] }
            })
            : [];

        const hasApiElement = (apiElement['setting-type'] != null);
        return {
            id: apiElement['id'],
            displayName: apiElement['elementDisplayName'],
            toolTip: apiElement['e-hint'],
            fieldTypeId: apiElement['ui-type']['id'],
            ordinal: apiElement['e-ordinal'],
            isRequired: apiElement['e-required'] === null ? false : apiElement['e-required'],
            systemName: apiElement['elementDisplayName'],
            sysName: apiElement['sysName'],
            unit: apiElement['e-units'],
            max: apiElement['e-max'],
            min: apiElement['e-min'],
            settingTypeOrdinal: !hasApiElement ? 0 : apiElement['setting-type']['st-ordinal'],
            options: options,
            settingDataId: apiElement['settingDataId'],
            sdValue: apiElement['sdValue']
        }
    });

    const O_OUTPUT_PORT_NO = 'O_OUTPUT_PORT_NO'
    const fields = fieldDescriptors.filter(item => !item.sysName?.includes(O_OUTPUT_PORT_NO));

    const groupElementFields = fields.sort((a, b) => {
        let fa = a.displayName, fb = b.displayName;
        return fa > fb ? -1 : fa < fb ? 1 : 0;
    });

    const firstElement = groupElementFields.shift() as any;
    groupElementFields.push(firstElement);
    groupElementFields.reverse();

    return groupElementFields;
}
